import React, { useState } from 'react';
import { X, Copy, CheckCircle, Code, Link, MessageSquare, Palette } from 'lucide-react';
import Toast from './Toast';
import ChatThemeCustomizer from './ChatThemeCustomizer';
import { withChatFormParams } from './chatEmbedFormUtils';

const COPY_LABELS = {
  url: 'URL',
  iframe: 'Embed code',
  minimalIframe: 'Minimal embed code',
  buttonIframe: 'Button embed code',
  scriptSnippet: 'JavaScript snippet',
  lightweightSnippet: 'Lightweight snippet',
};

const ChatEmbedModal = ({ agent, onClose }) => {
  const [copySuccess, setCopySuccess] = useState({
    url: false,
    iframe: false,
    minimalIframe: false,
    buttonIframe: false,
    scriptSnippet: false,
    lightweightSnippet: false,
  });
  const [toast, setToast] = useState(null);
  const [showThemeCustomizer, setShowThemeCustomizer] = useState(false);
  
  const baseUrl = window.location.origin;
  const embedUrl = withChatFormParams(`${baseUrl}/embed/chat/${agent.id}`, agent);
  const minimalEmbedUrl = withChatFormParams(`${baseUrl}/embed/chat/${agent.id}?view=minimal`, agent);
  const buttonEmbedUrl = withChatFormParams(`${baseUrl}/embed/chat/${agent.id}?view=button`, agent);
  const embedCode = `<iframe src="${embedUrl}" width="100%" height="700" frameborder="0" style="border-radius: 8px;"></iframe>`;
  const minimalEmbedCode = `<iframe src="${minimalEmbedUrl}" width="100%" height="720" frameborder="0" style="border-radius: 8px;"></iframe>`;
  const scriptSnippet = `(function() {
  var EMBED_URL = '${buttonEmbedUrl}';
  var EMBED_ORIGIN = new URL(EMBED_URL, window.location.href).origin;
  var iframe = document.createElement('iframe');
  iframe.src = EMBED_URL;
  iframe.setAttribute('allow', 'microphone');
  iframe.setAttribute('title', 'Chat widget');
  // Starts button-sized. Resized/repositioned live to match the widget's
  // real footprint (see message listener below) so it never reserves a big
  // invisible rectangle over the page while closed.
  iframe.style.cssText = 'position:fixed;bottom:24px;right:24px;width:64px;height:64px;border:none;z-index:999999;background:transparent;';
  document.body.appendChild(iframe);

  var CORNERS = {
    'bottom-right': ['bottom', 'right'],
    'bottom-left': ['bottom', 'left'],
    'top-right': ['top', 'right'],
    'top-left': ['top', 'left'],
  };
  var lastData = null;

  function applySize(data) {
    var margin = data.margin || 24;
    // Clamp against clientWidth/clientHeight (content area, excludes
    // scrollbars) rather than innerWidth/innerHeight (includes them), so the
    // widget never sizes itself into scrollbar space and stays stable when a
    // scrollbar toggles on the host page.
    var vw = document.documentElement.clientWidth || window.innerWidth;
    var vh = document.documentElement.clientHeight || window.innerHeight;
    var width = Math.min(data.width, vw - margin * 2);
    var height = Math.min(data.height, vh - margin * 2);
    iframe.style.top = iframe.style.bottom = iframe.style.left = iframe.style.right = 'auto';
    if (data.centered) {
      iframe.style.top = '50%';
      iframe.style.left = '50%';
      iframe.style.transform = 'translate(-50%, -50%)';
    } else {
      iframe.style.transform = 'none';
      var corner = CORNERS[data.position] || CORNERS['bottom-right'];
      iframe.style[corner[0]] = margin + 'px';
      iframe.style[corner[1]] = margin + 'px';
    }
    iframe.style.width = width + 'px';
    iframe.style.height = height + 'px';
  }

  window.addEventListener('message', function(event) {
    if (event.source !== iframe.contentWindow || event.origin !== EMBED_ORIGIN) return;
    if (!event.data || event.data.type !== 'braincx-chat-embed-resize') return;
    lastData = event.data;
    applySize(lastData);
  });

  window.addEventListener('resize', function() {
    if (lastData) applySize(lastData);
  });
})();`;

  // Lightweight loader snippet, modeled on the short Tawk.to-style pattern:
  // it only creates and inserts a <script> tag pointing at a static
  // chat-widget.js file that contains all of the logic above. Same
  // functionality, far less code pasted onto the client's page.
  const buttonUrlObj = new URL(buttonEmbedUrl, baseUrl);
  const loaderParams = new URLSearchParams(buttonUrlObj.search);
  loaderParams.delete('view');
  loaderParams.set('agent', agent.id);
  const lightweightLoaderSrc = `${baseUrl}/chat-widget.js?${loaderParams.toString()}`;
  const lightweightSnippet = `<script type="text/javascript">
var BraincxChat_API=BraincxChat_API||{}, BraincxChat_LoadStart=new Date();
(function(){
var s1=document.createElement("script"),s0=document.getElementsByTagName("script")[0];
s1.async=true;
s1.src='${lightweightLoaderSrc}';
s1.charset='UTF-8';
s0.parentNode.insertBefore(s1,s0);
})();
</script>`;

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess({ ...copySuccess, [type]: true });
      setToast({ message: `${COPY_LABELS[type] || 'Code'} copied to clipboard!`, type: 'success' });
      setTimeout(() => {
        setCopySuccess((prev) => ({ ...prev, [type]: false }));
      }, 2000);
    } catch (err) {
      setToast({ message: 'Failed to copy to clipboard', type: 'error' });
    }
  };

  const handlePreview = () => {
    window.open(buttonEmbedUrl, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-elegant">
        {/* Header */}
        <div className="bg-gradient-secondary text-white p-5 flex justify-between items-center rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <MessageSquare className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Embed Chat: {agent.name}</h2>
              <p className="text-white/70 text-xs mt-0.5 font-normal">Get embed code for chat widget</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Embed URL */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Link className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Chat Embed URL</h3>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-sm text-foreground break-all">
                {embedUrl}
              </div>
              <button
                onClick={() => handleCopy(embedUrl, 'url')}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2"
              >
                {copySuccess.url ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Use this URL to link directly to your agent's chat interface
              {agent.chat_embed_form_enabled && ' (includes pre-chat form fields)'}
            </p>
          </div>

          {/* Full Page Embed */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Full Page Embed (iframe)</h3>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground overflow-x-auto">
                <pre className="whitespace-pre-wrap break-words">{embedCode}</pre>
              </div>
              <button
                onClick={() => handleCopy(embedCode, 'iframe')}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2"
              >
                {copySuccess.iframe ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Embed the full chat interface in your website (recommended height: 700px)
            </p>
          </div>

          {/* Minimal Widget Embed */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Minimal Widget Embed (iframe)</h3>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground overflow-x-auto">
                <pre className="whitespace-pre-wrap break-words">{minimalEmbedCode}</pre>
              </div>
              <button
                onClick={() => handleCopy(minimalEmbedCode, 'minimalIframe')}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2"
              >
                {copySuccess.minimalIframe ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Compact widget version (recommended height: 720px)
            </p>
          </div>

          {/* Floating Button Embed */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Floating Button Embed (iframe)</h3>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground overflow-x-auto">
                <pre className="whitespace-pre-wrap break-words">{`<iframe src="${buttonEmbedUrl}" width="100%" height="100" frameborder="0" style="border-radius: 8px;"></iframe>`}</pre>
              </div>
              <button
                onClick={() => handleCopy(`<iframe src="${buttonEmbedUrl}" width="100%" height="100" frameborder="0" style="border-radius: 8px;"></iframe>`, 'buttonIframe')}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2"
              >
                {copySuccess.buttonIframe ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Floating chat button that opens a chat window when clicked (recommended height: 100px)
            </p>
          </div>

          {/* Lightweight Loader Snippet */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Lightweight Snippet (recommended)</h3>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground overflow-x-auto">
                <pre className="whitespace-pre-wrap break-words">{lightweightSnippet}</pre>
              </div>
              <button
                onClick={() => handleCopy(lightweightSnippet, 'lightweightSnippet')}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 self-start"
              >
                {copySuccess.lightweightSnippet ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              A short, tiny loader snippet (same pattern as widgets like Tawk.to) that inserts one external script tag instead of pasting the full widget code. It loads the same resizable floating chat button with identical behavior to the JavaScript Snippet below — just far less code on your page.
            </p>
          </div>

          {/* JavaScript Snippet */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">JavaScript Snippet</h3>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground overflow-x-auto max-h-40 overflow-y-auto">
                <pre className="whitespace-pre-wrap break-words">{`<script>
${scriptSnippet}
</script>`}</pre>
              </div>
              <button
                onClick={() => handleCopy(`<script>\n${scriptSnippet}\n</script>`, 'scriptSnippet')}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-2 self-start"
              >
                {copySuccess.scriptSnippet ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Add this script to your page. It automatically resizes to match the widget — a small button when closed, the full panel only while open — so it never blocks clicks or hover on the rest of your page.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 bg-muted/30 flex justify-between items-center">
          <button
            onClick={() => setShowThemeCustomizer(true)}
            className="px-4 py-2 bg-gradient-button hover:opacity-90 text-primary-foreground rounded-lg transition-all flex items-center gap-2"
          >
            <Palette className="w-4 h-4" />
            Customize Theme
          </button>
          <div className="flex gap-3">
            <button
              onClick={handlePreview}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors"
            >
              Preview
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
      
      {/* Theme Customizer Modal */}
      {showThemeCustomizer && (
        <ChatThemeCustomizer
          agent={agent}
          onClose={() => setShowThemeCustomizer(false)}
          onSave={(newSettings) => {
            // Update the agent object with new theme settings
            Object.assign(agent, newSettings);
            setShowThemeCustomizer(false);
          }}
        />
      )}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ChatEmbedModal;

